import { useEffect, type ReactNode } from 'react';

/**
 * The season picker the stats and standings pages share.
 *
 * Both pages are long enough that the season would otherwise scroll away before
 * the reader reaches what they came for, so the bar floats under the site
 * header. The negative margins let it span the full content width while the
 * layout's own padding stays.
 */
export function SeasonSelector({
  seasons,
  active,
  onSelect,
  label = (season: string) => season,
  aside,
  onVertical,
  hint = 'Arrow keys change the season',
}: {
  seasons: string[];
  active: string;
  onSelect: (season: string) => void;
  /** How a season reads on the tab; the stats page renames its all-seasons one. */
  label?: (season: string) => string;
  /** Anything that belongs beside the seasons, at the far end of the bar. */
  aside?: ReactNode;
  /**
   * What up and down do, when the page has something better for them than a
   * second way to change the season. The stats page walks its statistics with
   * them; without this they step the seasons like left and right.
   */
  onVertical?: (delta: -1 | 1) => void;
  /** The line under the bar saying what the arrow keys do. */
  hint?: string;
}) {
  const index = seasons.indexOf(active);
  const step = (delta: number) => {
    const next = seasons[index + delta];
    if (next) onSelect(next);
  };

  /**
   * Arrow keys walk the seasons: left back, right forward. Bound to the page
   * rather than to the tabs, because the reader is usually somewhere down a
   * long table when they want the next season — not on the bar itself.
   *
   * Up and down step the seasons too unless the page has claimed them for
   * something of its own.
   *
   * Anything typed into a field, and any combination carrying a modifier, is
   * left alone; those belong to whatever has focus.
   */
  useEffect(() => {
    const DELTAS: Record<string, { delta: -1 | 1; vertical: boolean }> = {
      ArrowLeft: { delta: -1, vertical: false },
      ArrowRight: { delta: 1, vertical: false },
      ArrowUp: { delta: -1, vertical: true },
      ArrowDown: { delta: 1, vertical: true },
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const pressed = DELTAS[event.key];
      if (!pressed) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      // A modal is its own context — moving the page under an open dialog is
      // never what the reader is asking for.
      if (document.querySelector('dialog[open]')) return;

      if (pressed.vertical && onVertical) {
        // Up and down would otherwise scroll the page out from under the move.
        event.preventDefault();
        onVertical(pressed.delta);
        return;
      }

      const next = seasons[seasons.indexOf(active) + pressed.delta];
      if (!next) return;

      event.preventDefault();
      onSelect(next);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [seasons, active, onSelect, onVertical]);

  return (
    <div
      className="bleed sticky top-[4.125rem] z-30 mb-6 border-b border-field-800 bg-field-950/95
        py-2.5 backdrop-blur"
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-ghost px-2"
          aria-label="Previous season"
          disabled={index <= 0}
          onClick={() => step(-1)}
        >
          ‹
        </button>

        <div role="tablist" aria-label="Season" className="flex flex-wrap gap-1">
          {seasons.map((season) => (
            <button
              key={season}
              type="button"
              role="tab"
              aria-selected={season === active}
              onClick={() => onSelect(season)}
              className={`px-4 py-1.5 font-display text-base font-semibold uppercase leading-none
                tracking-bug transition-colors ${
                  season === active
                    ? 'bg-brass-400 text-field-950'
                    : 'border border-field-700 bg-field-900 text-field-300 hover:border-brass-400 hover:text-brass-300'
                }`}
            >
              {label(season)}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="btn-ghost px-2"
          aria-label="Next season"
          disabled={index < 0 || index >= seasons.length - 1}
          onClick={() => step(1)}
        >
          ›
        </button>

        {aside ? <div className="ml-auto">{aside}</div> : null}
      </div>

      <p className="mt-1.5 font-display text-[0.6875rem] uppercase tracking-bug text-field-600">
        {hint}
      </p>
    </div>
  );
}
